// janus-engine/traps/skill-check.ts — Lane TRAPS S1 (2026-09-28): the trap verbs' FLAT skill check (arm / discern) in the browser.
//
// arm_trap_inc.php / discern_trap_inc.php call skill_roll_arr(is_target: false) — the caster rolls their routed skill (Machining for
// a mechanical trap, Magic for a magical one) against the flat Trap DC; no contestee. That core is Lane A's skill-roll twin
// (ability/skill-roll.ts skillRollArr + select-skill-for-contesting-roll.ts), IMPORTED unchanged — one twin of skill_roll_arr's math,
// never two (handoff §Cross-lane asks). This module only builds the JanusCtx it runs in and decides, BEFORE any die is drawn, whether
// the browser may roll at all (memory reference_client_truth_predraw_handback_fairness):
//   · the same check runs first on independent field views against synthetic extreme dice (Lane A's payloadDryRunRollSource patterns);
//   · any hand-back there, or a verdict shape the trap commit cannot write, makes the check UNMODELLED — decided on state alone, never
//     rolled anywhere: the commit reports it (the next rule to port) and refuses it (owner 2026-09-28: every trap check is solved in
//     the browser; the server-rolled lane is gone).
//   · the pre-roll CMRT dispatch runs IN the twin (skill-roll.ts frontEndNextRollCheck → janus() in trigger mode): a God-Touched PC's
//     Favor of the Gods spends a charge and its ADV lands on that check; held next-roll statuses are used up — the verdict carries those
//     writes and the commit records them.
// A served verdict is the rows (the skill_roll row, an unlucky_roll row, ascension rows, the CMRT carrier's rows), the actor's luck /
// reaction, the Fortunate or CMRT-carrier status, the carrier's charge spend and any ascension SP.
import { skillRollArr } from '../ability/skill-roll.js';
import { selectSkillForContestingRoll } from '../ability/select-skill-for-contesting-roll.js';
import { HandBack, Refusal, ecChanges, ecLean, type JanusCtx, type JanusReaders, type LogRow, type Row, type StatusRow } from '../ability/context.js';
import { payloadDryRunRollSource } from '../ability/environmental-payload.js';
import { recordingRollSource, type RecordedDraw, type RollSource } from '../actions/roll-source.js';
import { type JanusInput } from '../ability/janus.js';
import { frameCopyOnRead, type FrameCopy } from '../ability/frame-snapshot.js';
import { statusItemId, statusRemovalBefore } from '../statuses/expiry.js';

export const SKILL_ROLL_TYPE = 'ABF66E1E-DA79-4329-31FA-70D981032C06'; // RollType::getId('skill_roll')
export const MACHINING_SKILL = 'D7F51670-F6D9-5DD6-11D9-0CDBF35096A0';
export const MAGIC_SKILL = 'ABE36CBD-1142-9809-FA05-C7DC4263B43C';
export const DISCERN_TRAP = '44085184-61E8-595E-1430-1C6E2EA9F51B';
export const DISARM_TRAP = '074FF54B-BBC8-F90B-902B-A3F9ED99695D';
/** The rows a trap check may write (skill_roll_arr + caster_is_unlucky + check_ascension_skill_point_gain). */
export const TRAP_CHECK_ROW_TYPES = new Set([SKILL_ROLL_TYPE, 'unlucky_roll', 'gained_ascension_skill_point', 'failed_roll_for_ascension_skill_point', 'ascension_skill_tier_up',
  // caster_gains_luck's universal Fortunate (in combat, a natural 20 with a reaction to spend): its two log rows.
  'no_roll', 'reaction_ability_applied',
  // the pre-roll CMRT dispatch of a free ability (janus() in trigger mode: no_roll/free_ability, its applied row, the charge spend)
  'free_ability_applied', 'adjust_charges']);
/** The universal Fortunate status (reaction-inventory.ts FORTUNATE). */
export const FORTUNATE_STATUS = 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8';

const up = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

export interface TrapCheckInput {
  encounter: Row;
  /** The page's member rows keyed upper-case encounter_creature_id (viewed in isolation, never mutated). */
  members: ReadonlyMap<string, Row>;
  actorId: string;
  /** The host inventory row (roll_to). */
  host: Row;
  read: JanusReaders;
  skillId: string;
  /** skill_roll_arr's item_id_used: the Discern Trap ability (discern) or the host row itself (arm — PHP passes ['item_id' => host]). */
  itemIdUsed: string;
  dc: number;
  rollPrivate: number;
  /** Discern uses the shared raw boundary saver, including native status identities. */
  nativeSave?: boolean;
  savePreflight?: (save: TrapCheckSave) => void;
}
export interface TrapCheckSave {
  rows: LogRow[]; ec: Record<string, Row>; ecBefore: Record<string, Row>;
  statusesAdded: StatusRow[]; statusesRemoved: JanusCtx['statusesRemoved'];
  statusStacks: NonNullable<JanusCtx['statusStacks']>; charges: JanusCtx['chargesChanged'];
  playerCharactersChanged: NonNullable<JanusCtx['playerCharactersChanged']>;
}
export interface TrapCheckRolled {
  kind: 'rolled'; total: number; success: boolean; rows: LogRow[]; draws: RecordedDraw[];
  luck: { before: number; after: number } | null;
  /** In combat: the reaction the universal Fortunate spent (1 → 0), and the Fortunate status it applied (the commit writes it). */
  reaction: { before: number; after: number } | null;
  fortunate: { status_connected_id: string; ends: string } | null;
  pc: Array<{ item_id: string; skill_id: string; column: string; before: number; after: number }>;
  /** The CMRT dispatch's writes (Favor of the Gods): charge spends and the carrier applied as its own status. */
  charges: Array<{ holder: string; item_id: string; before: number; charges: number }>;
  statusesAdded: StatusRow[];
  /** Held next-roll statuses this check used up. */
  statusesRemoved: Array<{ status_item_id: string; target: string }>;
  save?: TrapCheckSave;
}
export type TrapCheckVerdict = TrapCheckRolled | { kind: 'unmodelled'; reason: string };

/** The definition reader the check runs with: arm's item_id_used is the bare host id (PHP hands skill_roll_arr ['item_id' => host],
 * no layers); Discern Trap's catalog row carries only `range` (S0 recon), so an unheld definition reads as that layer-less shell. */
function trapReaders(read: JanusReaders, itemIdUsed: string): JanusReaders {
  const shell = (id: string): Row => ({ items: { item_id: id, item_type: id === DISCERN_TRAP ? 'full_ability' : 'inventory' }, item_layer_types: {} });
  return { ...read, definition: (id: string) => {
    const held = read.definition(id);
    if (held) return held;
    return up(id) === up(itemIdUsed) ? shell(up(id)) : null;
  } };
}

function context(input: TrapCheckInput, roll: RollSource, copy: FrameCopy): JanusCtx {
  return {
    ...copy, originals: input.members,
    read: trapReaders(input.read, input.itemIdUsed), roll, rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [],
    statusConnectedId: null, rollPrivate: input.rollPrivate,
  };
}

/** One check through Lane A's skill_roll_arr twin. Throws HandBack / Refusal exactly as the twin does. */
function runCheck(ctx: JanusCtx, input: TrapCheckInput): number {
  const actor = ctx.members.get(up(input.actorId));
  if (!actor) throw new HandBack('trap_check:actor-not-held');
  const skill = selectSkillForContestingRoll(ctx, actor, [input.skillId]);
  if (skill.id !== up(input.skillId)) throw new HandBack('trap_check:skill-selection-moved');
  const janusInput: JanusInput = { casterId: up(input.actorId), targetIds: [], abilityId: up(input.itemIdUsed), rawDiceMod: 0, rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null };
  return skillRollArr(ctx, janusInput, actor, skill, clone(input.host), input.dc, { rollType: SKILL_ROLL_TYPE });
}

/** Supported CMRT carriers: Self-range free abilities with charges, resets, a calendar duration,
 * skill ADV/DIS and Skill-Roll triggers (Favor of the Gods). Preflight admits this support before any draw. */
const CMRT_CONTRACT_LAYERS = new Set(['ability_charges', 'ability_resets_long_rest', 'ability_resets_short_rest', 'duration_int', 'duration_type', 'range',
  'skill_rolls_adv', 'skill_rolls_dis', 'triggers_on_next_roll_type', 'triggers_when_caster_makes_roll_type', 'unenchantable']);
const RANGE_SELF = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const entryId = (e: unknown): string => up(typeof e === 'string' ? e.split('|')[0] : (e as Row | null)?.item_id ?? ((e as Row | null)?.items as Row | undefined)?.item_id);
export function isSkillRollCmrtCarrier(read: JanusReaders, itemId: string): boolean {
  const def = read.definition(up(itemId));
  if (!def || up((def.items as Row | undefined)?.item_type) !== 'FREE_ABILITY') return false;
  if (!(read.entries(def, 'triggers_when_caster_makes_roll_type') ?? []).some(e => entryId(e) === SKILL_ROLL_TYPE)) return false;
  if (Object.keys((def.item_layer_types as Row | undefined) ?? {}).some(k => !CMRT_CONTRACT_LAYERS.has(k))) return false;
  if (up(String(read.value(def, 'range') ?? '')).slice(0, 36) !== RANGE_SELF) return false;
  const raw = String(read.value(def, 'duration_int') ?? '').trim(); const n = /^\d{1,6}$/.test(raw) ? Number(raw) : 0; // a positive integer, as the commit
  return ['seconds', 'minutes', 'hours', 'days'].includes(String(read.value(def, 'duration_type') ?? '').toLowerCase()) && Number.isInteger(n) && n > 0;
}
/** A status whose triggers_on_next_roll_type lists Skill Roll (Favor of the Gods, Fortunate): a trap check uses it up. */
export function isSkillRollNextRollStatus(read: JanusReaders, itemId: string): boolean {
  const def = read.definition(up(itemId));
  return !!def && (read.entries(def, 'triggers_on_next_roll_type') ?? []).some(e => entryId(e) === SKILL_ROLL_TYPE);
}
function commitShape(ctx: JanusCtx, actorId: string): string | null {
  const aid = up(actorId);
  const cmrt = (id: unknown): boolean => isSkillRollCmrtCarrier(ctx.read, up(id));
  if (ctx.inventoryChanged.length) return 'trap_check:verdict-writes-more-than-the-check';
  // A held next-roll status this check used up (Favor of the Gods, Fortunate — the front-end rule in skill-roll.ts).
  if (ctx.statusesRemoved.some(r => up(r.target) !== aid || !isSkillRollNextRollStatus(ctx.read, r.status_item_id)))
    return 'trap_check:removal-other-than-a-used-up-next-roll-status';
  // A charge change's `before` is the pool at the START of the use (the commit compares it to the locked row); each spend of the same
  // pool lowers `charges` by one (a held Favor fires twice: carrier + status pool).
  const spends = new Map<string, number>();
  for (const c of ctx.chargesChanged) {
    const key = up(c.holder) + '|' + up(c.item_id);
    const n = (spends.get(key) ?? 0) + 1;
    spends.set(key, n);
    if (up(c.holder) !== aid || !cmrt(c.item_id) || c.charges !== c.before - n) return 'trap_check:charge-other-than-cmrt';
  }
  // A held carrier's re-fire leaves its row as is (S1 oracle `*_held`); a stack change is a shape the trap commit does not record.
  if ((ctx.statusStacks ?? []).length) return 'trap_check:status-stack';
  if (ctx.statusesAdded.some(st => up(st.target) !== aid || up(st.caster) !== aid || (up(st.status_item_id) !== FORTUNATE_STATUS && !cmrt(st.status_item_id))))
    return 'trap_check:status-other-than-fortunate-or-cmrt';
  if (ctx.statusesAdded.filter(st => up(st.status_item_id) === FORTUNATE_STATUS).length > 1) return 'trap_check:status-other-than-fortunate-or-cmrt';
  if (ctx.statusesAdded.some(st => st.status_ends_on_target_start_of_turn || st.status_ends_on_target_end_of_turn
      || st.status_ends_on_caster_start_of_next_turn || st.status_ends_on_caster_end_of_next_turn)) return 'trap_check:status-turn-end';
  if (ctx.rows.some(r => !TRAP_CHECK_ROW_TYPES.has(r.roll_type_1))) return 'trap_check:unexpected-row';
  if (ctx.rows.some(r => ['no_roll', 'reaction_ability_applied', 'free_ability_applied', 'adjust_charges'].includes(r.roll_type_1)
      && up(r.roll_item_id) !== FORTUNATE_STATUS && !cmrt(r.roll_item_id))) return 'trap_check:unexpected-row';
  if (ctx.rows.filter(r => r.roll_type_1 === SKILL_ROLL_TYPE).length !== 1) return 'trap_check:not-one-skill-row';
  const ec = ecChanges(ctx);
  for (const [id, cols] of Object.entries(ec)) {
    if (up(id) !== up(actorId)) return 'trap_check:writes-another-creature';
    if (Object.keys(cols).some(k => k !== 'luck' && k !== 'reaction')) return 'trap_check:writes-more-than-luck';
  }
  const actor = ctx.members.get(up(actorId));
  const pcId = up((actor?.items as Row | undefined)?.item_id ?? (actor?.encounter_creatures as Row | undefined)?.item_id);
  for (const change of ctx.playerCharactersChanged ?? []) if (up(change.item_id) !== pcId) return 'trap_check:ascension-elsewhere';
  return null;
}

/** Native rows, with exact persisted before identities rather than item-wide deletion instructions. */
function nativeSave(ctx: JanusCtx, input: TrapCheckInput): TrapCheckSave {
  const ec = ecChanges(ctx), ecBefore: Record<string, Row> = {};
  for (const [id, changes] of Object.entries(ec)) for (const column of Object.keys(changes))
    (ecBefore[id] ??= {})[column] = Number(ecLean(input.members.get(id)!, column) ?? 0);
  const statusesRemoved = ctx.statusesRemoved.flatMap(removal => {
    const original = input.members.get(up(removal.target));
    const held = original ? input.read.statuses(original).filter(status => removal.status_id
      ? up(input.read.value(status, 'status_id')) === up(removal.status_id) : statusItemId(status) === up(removal.status_item_id)) : [];
    if (!held.length) throw new HandBack('trap_check:status-before-not-held');
    return held.map(status => ({ status_id: up(input.read.value(status, 'status_id')), status_item_id: statusItemId(status),
      target: up(removal.target), before: statusRemovalBefore(status, removal.target) }));
  });
  return clone({ rows: ctx.rows, ec, ecBefore, statusesRemoved, // dnr-clone: allow size=one trap check's emitted save rows and changed scalar/status/charge/PC fields; why=detach lazy output leaves before the synchronous save preflight returns
    statusesAdded: ctx.statusesAdded.map(status => ({ ...status, status_id: status.status_id ?? input.read.uuid() })),
    statusStacks: ctx.statusStacks ?? [], charges: ctx.chargesChanged, playerCharactersChanged: ctx.playerCharactersChanged ?? [] });
}

const DRY_RUNS = ['max', 'min', 'alt-hi', 'alt-lo'] as const;

/**
 * Decide on STATE, then roll once. `roll` is the live source (browserRollSource); the dry runs never touch it.
 */
export function resolveTrapCheck(input: TrapCheckInput, roll: RollSource): TrapCheckVerdict {
  const copy = () => frameCopyOnRead(input.encounter, input.members, { catalogs: 'share' });
  for (const pattern of DRY_RUNS) {
    const dry = context(input, payloadDryRunRollSource(pattern), copy());
    try {
      runCheck(dry, input);
      if (input.nativeSave) {
        const save = nativeSave(dry, input);
        input.savePreflight?.(save);
      }
    }
    catch (error) {
      if (error instanceof HandBack) return { kind: 'unmodelled', reason: `preflight:${pattern}:${error.reason}` };
      if (error instanceof Refusal) return { kind: 'unmodelled', reason: `preflight:${pattern}:refusal` };
      return { kind: 'unmodelled', reason: `preflight:${pattern}:exception:${String((error as Error)?.message ?? error).slice(0, 80)}` };
    }
    const why = commitShape(dry, input.actorId);
    if (why) return { kind: 'unmodelled', reason: `preflight:${pattern}:${why}` };
  }
  // DRAWS. From here on nothing may hand the check back (the dry runs settled every state gate).
  const recording = recordingRollSource(roll);
  const ctx = context(input, recording, copy());
  const total = runCheck(ctx, input);
  // PHP quirk kept (S1 oracle): discern hands skill_roll_arr the FULL Discern Trap arr, whose id sits under `items`, and the row's
  // roll_item_id is read off the flat `item_id` key — so the legacy discern skill row carries an EMPTY roll_item_id. Arm's
  // ['item_id' => host] shape keeps the host id.
  if (up(input.itemIdUsed) === DISCERN_TRAP) for (const r of ctx.rows) if (r.roll_type_1 === SKILL_ROLL_TYPE) r.roll_item_id = null;
  const ec = ecChanges(ctx)[up(input.actorId)];
  const actor = input.members.get(up(input.actorId));
  const luckBefore = actor ? Number(ecLean(actor, 'luck') ?? 0) : 0;
  const reactionBefore = actor ? Number(ecLean(actor, 'reaction') ?? 0) : 0;
  const fortunate = ctx.statusesAdded.find(st => up(st.status_item_id) === FORTUNATE_STATUS) ?? null;
  return clone<TrapCheckRolled>({
    kind: 'rolled', total, success: total >= input.dc, rows: ctx.rows, draws: recording.draws,
    luck: ec && 'luck' in ec ? { before: Number.isFinite(luckBefore) ? luckBefore : 0, after: Number(ec.luck) } : null,
    reaction: ec && 'reaction' in ec ? { before: reactionBefore, after: Number(ec.reaction) } : null,
    fortunate: fortunate ? { status_connected_id: up(fortunate.status_connected_id), ends: String(fortunate.ends) } : null,
    pc: (ctx.playerCharactersChanged ?? []).map(c => ({ ...c })),
    charges: ctx.chargesChanged.map(c => ({ ...c })),
    statusesAdded: ctx.statusesAdded.filter(st => up(st.status_item_id) !== FORTUNATE_STATUS).map(st => ({ ...st })),
    statusesRemoved: ctx.statusesRemoved.map(r => ({ status_item_id: up(r.status_item_id), target: up(r.target) })),
    ...(input.nativeSave ? { save: nativeSave(ctx, input) } : {}),
  });
}

/** The routed skill for a trap's nature: Magic for a magical payload, else Machining. */
export function trapSkillFor(classification: string): string { return classification === 'magical' ? MAGIC_SKILL : MACHINING_SKILL; }
